import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import { IconCheck } from "../components/icons";
import heroContact from "../assets/images/hero/hero-contact.png";

// Responsive 2-column grid that collapses to 1 on narrow viewports
const ROW = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
  gap: "18px",
};

const LABEL = {
  display: "block",
  fontFamily: "var(--font-body)",
  fontWeight: 600,
  fontSize: "0.85rem",
  color: "var(--plum)",
  marginBottom: "8px",
  letterSpacing: "0.01em",
};

const REQUIRED_STAR = {
  color: "var(--rose)",
  marginLeft: "3px",
  fontWeight: 500,
};

const SECTION = {
  paddingBottom: "32px",
  marginBottom: "32px",
  borderBottom: "1px solid rgba(212,168,83,0.18)",
};

// Applied only to the final section — no bottom border or margin
const SECTION_LAST = {
  paddingBottom: 0,
  marginBottom: 0,
  borderBottom: "none",
};

const SECTION_HEADER = {
  display: "flex",
  alignItems: "baseline",
  gap: "14px",
  marginBottom: "22px",
};

const SECTION_NUMBER = {
  fontFamily: "var(--font-body)",
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "var(--gold)",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
};

const SECTION_TITLE = {
  fontFamily: "var(--font-display)",
  fontSize: "1.3rem",
  color: "var(--plum)",
  margin: 0,
  fontWeight: 600,
};

const STACK = {
  display: "grid",
  gap: "18px",
};

// Static lookup maps — module scope so they're not recreated on every effect run
const PACKAGE_MAP = {
  silly: "Silly & Playful",
  bright: "Bright & Confident",
  elegant: "Elegant & Timeless",
  specialist: "Specialist Experience",
  bespoke: "Bespoke Package",
};

const SPIRIT_MAP = {
  tequila: "Tequila",
  rum: "Rum / Tropical",
  gin: "Gin",
};

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  eventType: "",
  eventDate: "",
  eventTime: "",
  eventAddress: "",
  townCity: "",
  postCode: "",
  cocktailGuests: "",
  mocktailGuests: "",
  serviceType: "",
  packageSelection: "",
  specialistSpirit: "",
  message: "",
};

// Darkened gold for gradients — stays in the brand gold family
const GOLD_DARK = "#b8860b";

export default function Quote() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [guestError, setGuestError] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const errorRef = useRef(null);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pkg = params.get("package");
    const spirit = params.get("spirit");
    const updates = {};
    if (pkg && PACKAGE_MAP[pkg]) updates.packageSelection = PACKAGE_MAP[pkg];
    if (spirit && SPIRIT_MAP[spirit]) updates.specialistSpirit = SPIRIT_MAP[spirit];
    if (Object.keys(updates).length > 0) setForm(prev => ({ ...prev, ...updates }));
  }, [location.search]);

  // Scroll Netlify error banner into view when it appears
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error]);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    setGuestError(false);

    // Cross-field guest validation: if either guest field is filled, total must be > 0
    const cc = parseInt(form.cocktailGuests) || 0;
    const mc = parseInt(form.mocktailGuests) || 0;
    if ((form.cocktailGuests !== "" || form.mocktailGuests !== "") && cc + mc === 0) {
      setGuestError(true);
      setLoading(false);
      return;
    }

    const encode = (data) =>
      Object.keys(data)
        .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
        .join("&");

    // Exclude specialistSpirit from the payload when it isn't relevant
    const { specialistSpirit, ...baseForm } = form;
    const payload = {
      "form-name": "quote",
      ...baseForm,
      ...(form.packageSelection === "Specialist Experience" ? { specialistSpirit } : {}),
      agreedToTerms: agreedToTerms ? "Yes" : "No",
      marketingOptIn: marketingOptIn ? "Yes" : "No",
    };

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(payload),
      });
      if (!response.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const selectColor = (val) => ({ color: val ? "var(--text-primary)" : "var(--text-light)" });

  return (
    <main style={{ paddingTop: "var(--nav-h)" }}>
      {/* Hero */}
      <section className="sb-noise" style={{
        position: "relative", overflow: "hidden",
        backgroundImage: `url(${heroContact})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "clamp(60px, 10vw, 100px) 0 clamp(48px, 8vw, 80px)", textAlign: "center",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(160deg, rgba(58,24,68,0.75), rgba(80,30,60,0.68))",
          zIndex: 0,
        }} />
        <div className="sb-container" style={{ position: "relative", zIndex: 2 }}>
          <Reveal><span style={{ display: "inline-block", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: "0.78rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold-light)", marginBottom: "12px" }}>Get a Quote</span></Reveal>
          <Reveal delay={0.1}><h1 style={{ color: "var(--white)", maxWidth: "600px", margin: "0 auto 16px" }}>Get Your <span style={{ color: "var(--gold)" }}>Tailored Quote</span></h1></Reveal>
          <Reveal delay={0.15}><p style={{ color: "rgba(255,255,255,0.65)", maxWidth: "520px", margin: "0 auto", fontSize: "1.05rem" }}>Fill in your event details and we'll send you a personalised quote with everything you need to know for your celebration.</p></Reveal>
        </div>
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "80px", background: "linear-gradient(to top, var(--blush-tint), transparent)" }} />
      </section>

      {/* Form (centered, primary focus) */}
      <section className="sb-section">
        <div className="sb-container">
          <div style={{ maxWidth: "780px", margin: "0 auto" }}>
            <Reveal>
              <div style={{
                background: "var(--white)", borderRadius: "var(--radius-lg)",
                padding: "clamp(32px, 5vw, 56px)",
                boxShadow: "var(--shadow-md)",
                border: "1px solid rgba(212,168,83,0.08)",
              }}>
                {submitted ? (
                  <div style={{ textAlign: "center", padding: "40px 0 32px" }}>
                    {/* Icon */}
                    <div style={{
                      width: "64px", height: "64px", borderRadius: "50%",
                      background: `linear-gradient(135deg, var(--gold), ${GOLD_DARK})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      margin: "0 auto 24px",
                      boxShadow: "0 4px 20px rgba(212,168,83,0.35)",
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>

                    {/* Headline */}
                    <h3 style={{ color: "var(--plum)", marginBottom: "10px", fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>Thank you!</h3>
                    <p style={{ color: "var(--text-secondary)", maxWidth: "460px", margin: "0 auto 32px", lineHeight: 1.6 }}>
                      We've received your quote request and will review your event details carefully.
                    </p>

                    {/* What happens next */}
                    <div style={{
                      background: "var(--blush-glow)",
                      border: "1px solid rgba(212,168,83,0.18)",
                      borderRadius: "var(--radius-md)",
                      padding: "24px 28px",
                      textAlign: "left",
                      maxWidth: "480px",
                      margin: "0 auto 28px",
                    }}>
                      <p style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 600,
                        fontSize: "1rem",
                        color: "var(--plum)",
                        margin: "0 0 16px",
                      }}>
                        What happens next?
                      </p>
                      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "12px" }}>
                        {[
                          "We review your event details",
                          "We prepare a tailored quote for your celebration",
                          "We email you with pricing, availability, and the next steps",
                        ].map((step, i) => (
                          <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                            <span style={{
                              flexShrink: 0,
                              width: "22px", height: "22px",
                              borderRadius: "50%",
                              background: "var(--gold)",
                              color: "var(--white)",
                              fontFamily: "var(--font-body)",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              marginTop: "1px",
                            }}>
                              {i + 1}
                            </span>
                            <span style={{ fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* CTAs */}
                    <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                      <Link to="/" className="sb-btn sb-btn-primary">Back to Home</Link>
                      <Link to="/experience" className="sb-btn sb-btn-outline">View Our Experiences</Link>
                    </div>
                  </div>
                ) : (
                  <form name="quote" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={handleSubmit}>
                    <input type="hidden" name="form-name" value="quote" />
                    <div style={{ display: "none" }}>
                      <input name="bot-field" tabIndex="-1" aria-hidden="true" />
                    </div>

                    {/* ── Section 01: Your Details ── */}
                    <div style={SECTION}>
                      <div style={SECTION_HEADER}>
                        <span style={SECTION_NUMBER}>01</span>
                        <h3 style={SECTION_TITLE}>Your Details</h3>
                      </div>
                      <div style={STACK}>
                        <div style={ROW}>
                          <div>
                            <label style={LABEL} htmlFor="q-firstName">
                              First Name<span style={REQUIRED_STAR}>*</span>
                            </label>
                            <input id="q-firstName" name="firstName" className="sb-input" type="text" maxLength={60} value={form.firstName} onChange={handleChange("firstName")} required autoComplete="given-name" />
                          </div>
                          <div>
                            <label style={LABEL} htmlFor="q-lastName">
                              Last Name<span style={REQUIRED_STAR}>*</span>
                            </label>
                            <input id="q-lastName" name="lastName" className="sb-input" type="text" maxLength={60} value={form.lastName} onChange={handleChange("lastName")} required autoComplete="family-name" />
                          </div>
                        </div>
                        <div style={ROW}>
                          <div>
                            <label style={LABEL} htmlFor="q-email">
                              Email Address<span style={REQUIRED_STAR}>*</span>
                            </label>
                            <input id="q-email" name="email" className="sb-input" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange("email")} required autoComplete="email" />
                          </div>
                          <div>
                            <label style={LABEL} htmlFor="q-phone">
                              Phone Number<span style={REQUIRED_STAR}>*</span>
                            </label>
                            <input id="q-phone" name="phone" className="sb-input" type="tel" placeholder="e.g. 07912 345678" value={form.phone} onChange={handleChange("phone")} required autoComplete="tel" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Section 02: Event Details ── */}
                    <div style={SECTION}>
                      {/* Reduced marginBottom on this header to make room for the helper text below */}
                      <div style={{ ...SECTION_HEADER, marginBottom: "10px" }}>
                        <span style={SECTION_NUMBER}>02</span>
                        <h3 style={SECTION_TITLE}>Event Details</h3>
                      </div>
                      <p style={{ fontSize: "0.82rem", color: "var(--text-light)", marginBottom: "20px" }}>
                        Don't worry if you're unsure — just fill in what you can.
                      </p>
                      <div style={STACK}>
                        <div>
                          <label style={LABEL} htmlFor="q-eventType">Event Type</label>
                          <select id="q-eventType" name="eventType" className="sb-input" value={form.eventType} onChange={handleChange("eventType")} style={selectColor(form.eventType)}>
                            <option value="">Select event type</option>
                            <option value="henParty">Hen Party</option>
                            <option value="birthday">Birthday</option>
                            <option value="corporateEvent">Corporate Event</option>
                            <option value="privateCelebration">Private Celebration</option>
                            <option value="teamSocial">Team Social</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div style={ROW}>
                          <div>
                            <label style={LABEL} htmlFor="q-eventDate">Event Date</label>
                            <input
                              id="q-eventDate" name="eventDate" className="sb-input sb-input-datetime"
                              type="date"
                              min={new Date().toISOString().split("T")[0]}
                              value={form.eventDate} onChange={handleChange("eventDate")}
                            />
                          </div>
                          <div>
                            <label style={LABEL} htmlFor="q-eventTime">Preferred Time</label>
                            <input
                              id="q-eventTime" name="eventTime" className="sb-input sb-input-datetime"
                              type="time"
                              value={form.eventTime} onChange={handleChange("eventTime")}
                            />
                          </div>
                        </div>
                        <div>
                          <label style={LABEL} htmlFor="q-eventAddress">Event Address</label>
                          <input id="q-eventAddress" name="eventAddress" className="sb-input" type="text" maxLength={120} placeholder="Street address" value={form.eventAddress} onChange={handleChange("eventAddress")} autoComplete="street-address" />
                        </div>
                        <div style={ROW}>
                          <div>
                            <label style={LABEL} htmlFor="q-townCity">Town / City</label>
                            <input id="q-townCity" name="townCity" className="sb-input" type="text" maxLength={60} placeholder="e.g. London" value={form.townCity} onChange={handleChange("townCity")} autoComplete="address-level2" />
                          </div>
                          <div>
                            <label style={LABEL} htmlFor="q-postCode">Postcode</label>
                            <input id="q-postCode" name="postCode" className="sb-input" type="text" maxLength={10} placeholder="e.g. SW1A 1AA" value={form.postCode} onChange={handleChange("postCode")} autoComplete="postal-code" />
                          </div>
                        </div>
                        <div style={ROW}>
                          <div>
                            <label style={LABEL} htmlFor="q-cocktailGuests">Number of Cocktail Guests</label>
                            <input id="q-cocktailGuests" name="cocktailGuests" className="sb-input" type="number" min="0" max="100" placeholder="e.g. 12" value={form.cocktailGuests} onChange={handleChange("cocktailGuests")} />
                          </div>
                          <div>
                            <label style={LABEL} htmlFor="q-mocktailGuests">Number of Mocktail Guests</label>
                            <input id="q-mocktailGuests" name="mocktailGuests" className="sb-input" type="number" min="0" max="100" placeholder="e.g. 0" value={form.mocktailGuests} onChange={handleChange("mocktailGuests")} />
                          </div>
                        </div> 
                        {guestError && (
                          <p role="alert" style={{ fontSize: "0.85rem", color: "#C0392B", marginTop: "-6px" }}>
                            Please enter at least 1 guest in total.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ── Section 03: Service & Package Selection ── */}
                    <div style={SECTION}>
                      <div style={SECTION_HEADER}>
                        <span style={SECTION_NUMBER}>03</span>
                        <h3 style={SECTION_TITLE}>Service & Package Selection</h3>
                      </div>
                      <div style={STACK}>
                        <div>
                          <label style={LABEL} htmlFor="q-serviceType">Service Type</label>
                          <select id="q-serviceType" name="serviceType" className="sb-input" value={form.serviceType} onChange={handleChange("serviceType")} style={selectColor(form.serviceType)}>
                            <option value="">Select service type</option>
                            <option value="cocktailMakingClass">Cocktail Making Class</option>
                            <option value="cocktailService">Cocktail Service</option>
                          </select>
                        </div>
                        <div>
                          <label style={LABEL} htmlFor="q-packageSelection">Package Selection</label>
                          <select
                            id="q-packageSelection"
                            name="packageSelection"
                            className="sb-input"
                            value={form.packageSelection}
                            onChange={(e) => {
                              const val = e.target.value;
                              setForm(prev => ({
                                ...prev,
                                packageSelection: val,
                                // Clear spirit when switching away from Specialist Experience
                                ...(val !== "specialistExperience" && { specialistSpirit: "" }),
                              }));
                            }}
                            style={selectColor(form.packageSelection)}
                          >
                            <option value="">Select a package</option>
                            <option value="brightConfident">Bright &amp; Confident</option>
                            <option value="sillyPlayful">Silly &amp; Playful — Most Popular</option>
                            <option value="elegantTimeless">Elegant &amp; Timeless</option>
                            <option value="specialistExperience">Specialist Experience</option>
                            <option value="bespokePackage">Bespoke Package</option>
                          </select>
                        </div>
                        {form.packageSelection === "bespokePackage" && (
                          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "12px 14px", background: "var(--blush-glow)", borderRadius: "var(--radius-sm)", border: "1px solid rgba(212,168,83,0.2)" }}>
                            <span style={{ color: "var(--gold)", flexShrink: 0, marginTop: "1px" }}>✦</span>
                            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                              Tell us more about your bespoke request in the <strong style={{ color: "var(--plum)" }}>Additional Information</strong> section below so we can tailor the experience to your needs.
                            </p>
                          </div>
                        )}
                        {form.packageSelection === "specialistExperience" && (
                          <div>
                            <label style={LABEL} htmlFor="q-specialistSpirit">Specialist Spirit</label>
                            <select id="q-specialistSpirit" name="specialistSpirit" className="sb-input" value={form.specialistSpirit} onChange={handleChange("specialistSpirit")} style={selectColor(form.specialistSpirit)}>
                              <option value="">Select a spirit</option>
                              <option value="Tequila">Tequila</option>
                              <option value="Rum / Tropical">Rum / Tropical</option>
                              <option value="Gin">Gin</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Section 04: Additional Information ── */}
                    <div style={SECTION_LAST}>
                      <div style={SECTION_HEADER}>
                        <span style={SECTION_NUMBER}>04</span>
                        <h3 style={SECTION_TITLE}>Additional Information</h3>
                      </div>
                      <div>
                        <textarea
                          id="q-message"
                          name="message"
                          className="sb-input"
                          rows={5}
                          aria-label="Message, special requests, dietary requirements — anything else we should know"
                          value={form.message}
                          onChange={handleChange("message")}
                          placeholder="Message, special requests, dietary requirements — anything else we should know…"
                        />
                      </div>
                    </div>

                    {/* ── Submit ── */}
                    <div style={{ marginTop: "36px" }}>
                      {/* Terms & marketing checkboxes */}
                      <div style={{ display: "grid", gap: "14px", marginBottom: "24px" }}>
                        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                          <input
                            type="checkbox"
                            id="q-terms"
                            name="agreedToTerms"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                            required
                            style={{ width: "18px", height: "18px", accentColor: "var(--gold)", marginTop: "2px", flexShrink: 0, cursor: "pointer" }}
                          />
                          <label htmlFor="q-terms" style={{ fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.5, cursor: "pointer" }}>
                            I agree to the{" "}
                            {/* Use <a> with target="_blank" so React Router doesn't navigate away and lose form state */}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)", textDecoration: "underline", textUnderlineOffset: "2px" }}>Terms &amp; Conditions</a>
                            {" "}and{" "}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)", textDecoration: "underline", textUnderlineOffset: "2px" }}>Privacy Policy</a>
                            <span style={REQUIRED_STAR} aria-hidden="true">*</span>
                          </label>
                        </div>
                        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                          <input
                            type="checkbox"
                            id="q-marketing"
                            name="marketingOptIn"
                            checked={marketingOptIn}
                            onChange={(e) => setMarketingOptIn(e.target.checked)}
                            style={{ width: "18px", height: "18px", accentColor: "var(--gold)", marginTop: "2px", flexShrink: 0, cursor: "pointer" }}
                          />
                          <label htmlFor="q-marketing" style={{ fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.5, cursor: "pointer" }}>
                            I'd like to receive updates and offers from Silly Butterfly.
                          </label>
                        </div>
                      </div>

                      <div ref={errorRef} role="alert" aria-live="polite">
                        {error && (
                          <p style={{ fontSize: "0.88rem", color: "#C0392B", textAlign: "center", padding: "10px 14px", background: "rgba(192,57,43,0.07)", borderRadius: "var(--radius-sm)", margin: "0 0 16px" }}>
                            Something went wrong. Please try again or email us at{" "}
                            <a href="mailto:hello@sillybutterfly.com" style={{ color: "#C0392B" }}>hello@sillybutterfly.com</a>.
                          </p>
                        )}
                      </div>

                      <button className="sb-btn sb-btn-primary" type="submit" disabled={loading} style={{ width: "100%", justifyContent: "center", opacity: loading ? 0.7 : 1 }}>
                        {loading ? "Sending…" : "Get My Quote →"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>

            {/* Supporting trust card — hidden after submission (no longer relevant) */}
            {!submitted && (
              <Reveal delay={0.15}>
                <div style={{
                  marginTop: "clamp(32px, 5vw, 48px)",
                  background: "var(--blush-glow)",
                  borderRadius: "var(--radius-lg)",
                  padding: "clamp(24px, 4vw, 36px)",
                  border: "1px solid rgba(212,168,83,0.14)",
                }}>
                  <h4 style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "1.1rem",
                    color: "var(--plum)",
                    margin: "0 0 20px",
                    textAlign: "center",
                    fontWeight: 600,
                  }}>
                    Why Get a Quote with Silly Butterfly?
                  </h4>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                    gap: "16px",
                  }}>
                    {[
                      "No obligation — just a friendly, tailored quote",
                      "We usually respond within 24 hours",
                      "Flexible packages to suit any budget",
                      "Personalised service from first enquiry to event day",
                      "Need a quicker reply? Message us on WhatsApp.",
                    ].map((p, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "flex-start", gap: "10px",
                        fontSize: "0.9rem", color: "var(--text-secondary)",
                        lineHeight: 1.5,
                      }}>
                        <span style={{ color: "var(--gold)", marginTop: "2px", flexShrink: 0 }}><IconCheck /></span>
                        <span>{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
